import React, { ComponentProps, forwardRef } from 'react';
import { Select } from '@mezzoforte/forge';
import { groupBy, sortBy } from 'lodash-es';
import manufacturersJSON from '@/data/manufacturers.json';
import { EntryListAPIResponse } from '@/types/ApiResponse';

interface Props extends ComponentProps<typeof Select> {
  manufacturers: EntryListAPIResponse['aggregations']['manufacturers'];
  topManufacturers?: string[];
}

const normalizeString = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');

const mapManufacturers = (manufacturers: EntryListAPIResponse['aggregations']['manufacturers']) =>
  manufacturers.map(({ key, docCount: count }) => ({
    key,
    count,
    name:
      [...manufacturersJSON.passengerCar, ...manufacturersJSON.machinery].find(
        (m) => normalizeString(m) === normalizeString(key)
      ) ?? key,
  }));

export const ListPageManufacturerSelect = forwardRef<HTMLSelectElement, Props>(
  ({ manufacturers: aggregationsManufacturers, topManufacturers, ...props }, ref) => {
    const manufacturers = mapManufacturers(aggregationsManufacturers);

    const groupedManufacturers = groupBy(manufacturers, ({ name }) => name.charAt(0).toUpperCase());

    const groupedOptions = [
      ...(topManufacturers?.length
        ? [
            {
              name: 'Suosituimmat',
              options: topManufacturers
                .filter((name) => manufacturers.some((m) => m.name === name))
                .map((name) => {
                  const manufacturer = manufacturers.find((m) => m.name === name);
                  return {
                    key: manufacturer?.key ?? name,
                    name,
                    count: manufacturer?.count ?? 0,
                  };
                }),
            },
          ]
        : []),
      ...sortBy(Object.entries(groupedManufacturers), ([char, _]) => char).map(([char, manufacturers]) => ({
        name: char,
        options: manufacturers,
      })),
    ];

    return (
      <Select
        ref={ref}
        {...props}
      >
        <>
          <option value="">Kaikki</option>
          {groupedOptions.map((group) => (
            <optgroup
              key={group.name}
              label={group.name}
            >
              {group.options.map((option) => (
                <option
                  key={option.key}
                  value={option.key.toLowerCase()}
                >
                  {option.name}
                </option>
              ))}
            </optgroup>
          ))}
        </>
      </Select>
    );
  }
);
